import classnames from 'classnames';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { isProjects, isSortedByDirection, isSortSelected } from '../../../../../shared/projectsV2';
import { useIsQualityEnabled, useIsSecurityEnabled } from '../../../../helpers/features';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import Icon, { icons } from '../../../generic/Icon';
import useFetchProjectsAction from '../hooks/useFetchProjectsAction';
import type { ProjectCategory, ProjectsSort } from '../../../../../shared/projectsV2';
import type { IconClassName } from '../../../generic/Icon';

import IssuesSortingDropdown from './IssuesSortingDropdown';
import RevisionSortingDropdown from './RevisionSortingDropdown';
import SelectAllProjectsCheckbox from './SelectAllProjectsCheckbox';

type CategoryConfig = {
  title: string;
  issues: string;
  icon?: IconClassName;
};

const CATEGORY_CONFIG: Record<ProjectCategory, CategoryConfig> = {
  projects: {
    title: 'Title',
    issues: 'Total issues',
    icon: icons.globe,
  },
  releaseGroups: {
    title: 'Title',
    issues: 'Issues',
  },
  sharedProjects: {
    title: 'Shared Projects',
    issues: 'Total issues',
    icon: icons.globe,
  },
};

const ProjectsTableHeader: VoidFunctionComponent = () => {
  const category: ProjectCategory = useSelector(ProjectsV2Store.selectors.getCategory);

  const sort: ProjectsSort = useSelector(ProjectsV2Store.selectors.getSort);
  const setSort = useFetchProjectsAction(ProjectsV2Store.actions.setSort);

  const hasSecurity = useIsSecurityEnabled();
  const hasQuality = useIsQualityEnabled();
  const isSecurityOrQualityEnabled = hasSecurity || hasQuality;
  const icon = isSortedByDirection(sort, 'desc') ? icons.angleDown : icons.angleUp;

  return (
    <div className="projects-table__header">
      <div>
        <SelectAllProjectsCheckbox />
      </div>
      <div>
        <Button
          type="text"
          isFullWidth
          onClick={() => setSort(sort === 'title_asc' ? 'title_desc' : 'title_asc')}>
          <div className={classnames({ 'projects-table__header_active': isSortSelected(sort, 'title') })}>
            <Flex justify="space-between">
              {CATEGORY_CONFIG[category].title}
              {isSortSelected(sort, 'title') &&
                <Icon icon={icon} size="fa-lg" />}
            </Flex>
          </div>
        </Button>
      </div>
      {isProjects(category) && isSecurityOrQualityEnabled ?
        <IssuesSortingDropdown /> :
        <div>
          <Button
            type="text"
            isFullWidth
            onClick={() => setSort(sort === 'issues-total_asc' ? 'issues-total_desc' : 'issues-total_asc')}>
            <div className={classnames({ 'projects-table__header_active': isSortSelected(sort, 'issues-total') })}>
              <Flex justify="space-between">
                <span className="projects-table__header__issues__label">
                  {
                    CATEGORY_CONFIG[category]?.icon
                    && <Icon icon={CATEGORY_CONFIG[category].icon as IconClassName} />
                  }
                  { CATEGORY_CONFIG[category].issues }
                </span>
                {isSortSelected(sort, 'issues-total') &&
                  <Icon icon={icon} size="fa-lg" />}
              </Flex>
            </div>
          </Button>
        </div>}
      <RevisionSortingDropdown />
    </div>
  );
};

export default ProjectsTableHeader;
